import React from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card, CardContent } from '../../components/ui/card';
import { FileText, Users, AlertTriangle, CheckCircle2, XCircle, Scale, Mail, AlertCircle } from 'lucide-react';

const TermsOfService = () => {
  const lastUpdated = "20 January 2026";

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-[#42b1c2]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-[#42b1c2]" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
            <p className="text-gray-600">Last updated: {lastUpdated}</p>
          </div>

          {/* Important Notice */}
          <Card className="mb-8 border-amber-200 bg-amber-50">
            <CardContent className="p-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800">
                    <strong>Important:</strong> These Terms of Service are provided for informational purposes and govern your use of the 
                    Luma Med Global platform. This document does not constitute legal advice. By using our platform, you agree to be 
                    bound by these terms. If you do not agree, please do not use our services.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content */}
          <div className="prose prose-gray max-w-none">
            
            {/* Introduction */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Scale className="h-5 w-5 text-[#42b1c2] mr-2" />
                  Agreement Overview
                </h2>
                <p className="text-gray-600 mb-4">
                  Welcome to Luma Med Global. These Terms of Service ("Terms") constitute a legally binding agreement between you 
                  ("User", "you", or "your") and Luma Med Global ("Company", "we", "our", or "us") regarding your access to and 
                  use of our recruitment platform, website, and related services.
                </p>
                <p className="text-gray-600">
                  Luma Med Global is a medically led recruitment platform that facilitates connections between healthcare 
                  professionals and healthcare employers, primarily in Saudi Arabia and the Middle East.
                </p>
              </CardContent>
            </Card>

            {/* Nature of Service */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Users className="h-5 w-5 text-[#42b1c2] mr-2" />
                  Nature of Our Service
                </h2>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 font-medium">
                    Luma Med Global is a medically led healthcare recruitment company specialising in the recruitment 
                    and placement of international medical professionals. We combine clinical expertise with 
                    technology-enabled tools to support ethical, transparent, and compliant healthcare recruitment 
                    across Saudi Arabia and the Middle East.
                  </p>
                </div>
                
                <p className="text-gray-600 mb-4">
                  Our role is to:
                </p>
                <ul className="space-y-2 text-gray-600 mb-4">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Recruit and place qualified healthcare professionals with verified credentials</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Partner with hospitals and healthcare employers to support compliant hiring</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Use AI-supported tools to enhance screening, matching, and recruitment workflows</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Provide guidance and support throughout international relocation and onboarding</span>
                  </li>
                </ul>
                
                <p className="text-gray-600">
                  <strong>Important:</strong> Final hiring decisions and employment contracts are agreed directly 
                  between employers and candidates. Luma Med Global supports the recruitment process but does not 
                  act as the employing entity.
                </p>
              </CardContent>
            </Card>

            {/* No Guarantee */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mr-2" />
                  No Guarantee of Employment
                </h2>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <p className="text-amber-800">
                    <strong>Luma Med Global does not guarantee job placement, interviews, or employment outcomes.</strong>
                  </p>
                </div>
                
                <p className="text-gray-600 mb-4">
                  While we strive to match qualified candidates with suitable opportunities, you acknowledge that:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Creating a profile does not guarantee that employers will contact you</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Employer interest does not guarantee an interview or job offer</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>AI-assisted matching and feedback are advisory tools, not guarantees of suitability</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Final hiring decisions rest entirely with the employing organisation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Visa approvals, licensing, and credential verification are subject to regulatory authorities</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* User Responsibilities */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">User Responsibilities</h2>
                
                <h3 className="text-lg font-medium text-gray-800 mb-3">For Candidates</h3>
                <p className="text-gray-600 mb-4">As a candidate using our platform, you agree to:</p>
                <ul className="space-y-2 text-gray-600 mb-6">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Provide accurate information:</strong> All information in your profile, CV, and credentials must be truthful, accurate, and up-to-date</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Maintain valid credentials:</strong> You are responsible for maintaining current professional licences and certifications</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Respond professionally:</strong> Communicate respectfully with employers and platform staff</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Protect your account:</strong> Keep your login credentials secure and confidential</span>
                  </li>
                </ul>
                
                <h3 className="text-lg font-medium text-gray-800 mb-3">For Employers</h3>
                <p className="text-gray-600 mb-4">As an employer using our platform, you agree to:</p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Post legitimate opportunities:</strong> All job postings must represent genuine employment opportunities</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Respect candidate data:</strong> Use candidate information only for recruitment purposes</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Communicate honestly:</strong> Provide accurate information about roles, compensation, and working conditions</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Comply with laws:</strong> Adhere to all applicable employment and anti-discrimination laws</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Prohibited Conduct */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <XCircle className="h-5 w-5 text-red-500 mr-2" />
                  Prohibited Conduct
                </h2>
                <p className="text-gray-600 mb-4">
                  The following activities are strictly prohibited and may result in immediate account suspension or termination:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <XCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-1" />
                    <span>Submitting false, misleading, or fraudulent information</span>
                  </li>
                  <li className="flex items-start">
                    <XCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-1" />
                    <span>Impersonating another person or misrepresenting your identity</span>
                  </li>
                  <li className="flex items-start">
                    <XCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-1" />
                    <span>Using the platform for any unlawful purpose</span>
                  </li>
                  <li className="flex items-start">
                    <XCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-1" />
                    <span>Harassing, threatening, or abusing other users or staff</span>
                  </li>
                  <li className="flex items-start">
                    <XCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-1" />
                    <span>Attempting to circumvent security measures or access unauthorised areas</span>
                  </li>
                  <li className="flex items-start">
                    <XCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-1" />
                    <span>Scraping, copying, or redistributing platform content without permission</span>
                  </li>
                  <li className="flex items-start">
                    <XCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-1" />
                    <span>Using the platform to collect candidate data for purposes other than legitimate recruitment</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Account Suspension */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Suspension & Termination</h2>
                <p className="text-gray-600 mb-4">
                  We reserve the right to suspend or terminate your account at our discretion if we believe you have 
                  violated these Terms, engaged in prohibited conduct, or if continued access poses a risk to the platform 
                  or other users.
                </p>
                <p className="text-gray-600 mb-4">
                  Upon termination:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Your access to the platform will be revoked immediately</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>You may request deletion of your personal data in accordance with our Privacy Policy</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Any outstanding fees or obligations remain payable</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Intellectual Property */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Intellectual Property</h2>
                <p className="text-gray-600 mb-4">
                  All content on the Luma Med Global platform, including but not limited to text, graphics, logos, icons, 
                  images, and software, is the property of Luma Med Global or its licensors and is protected by intellectual 
                  property laws.
                </p>
                <p className="text-gray-600">
                  You retain ownership of content you submit (such as your CV and profile information), but grant us a 
                  licence to use this content for the purposes of providing our services.
                </p>
              </CardContent>
            </Card>

            {/* Limitation of Liability */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Limitation of Liability</h2>
                <p className="text-gray-600 mb-4">
                  To the maximum extent permitted by law:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Our platform is provided "as is" without warranties of any kind</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>We are not liable for any indirect, incidental, or consequential damages arising from your use of the platform</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>We are not responsible for the actions, decisions, or conduct of employers or other users</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>We do not guarantee the accuracy of information provided by third parties</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Governing Law */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Governing Law</h2>
                <p className="text-gray-600">
                  These Terms shall be governed by and construed in accordance with the laws of England and Wales. 
                  Any disputes arising from these Terms or your use of the platform shall be subject to the exclusive 
                  jurisdiction of the courts of England and Wales.
                </p>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="mb-6 border-[#42b1c2]/30 bg-[#42b1c2]/5">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Mail className="h-5 w-5 text-[#42b1c2] mr-2" />
                  Contact Us
                </h2>
                <p className="text-gray-600 mb-4">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-gray-700"><strong>Email:</strong> info@lumamedglobal.com</p>
                </div>
              </CardContent>
            </Card>

            {/* Updates */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Changes to These Terms</h2>
                <p className="text-gray-600">
                  We may update these Terms from time to time. We will notify you of any material changes by posting 
                  the updated Terms on our platform with a new "Last updated" date. Your continued use of the platform 
                  after such changes constitutes acceptance of the revised Terms.
                </p>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfService;
